import axios from "axios";
import { create as createDiffPatcher } from "jsondiffpatch";

const jdp = createDiffPatcher({
  objectHash: (obj) =>
    obj?.id || obj?.uuid || obj?.code || obj?.value || obj?.label || JSON.stringify(obj),
  arrays: { detectMove: false },
});

function safeParseJSON(s) {
  try { return JSON.parse(s); } catch { return undefined; }
}

function decodeJwtPayload(token) {
  try {
    const [, payload] = token.split(".");
    if (!payload) return undefined;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "===".slice((base64.length + 3) % 4);
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return undefined;
  }
}

function getUserId() {
  const rawUser = localStorage.getItem("userData") || localStorage.getItem("user");
  const parsedUser = safeParseJSON(rawUser);
  if (parsedUser?.userId) return parsedUser.userId;
  if (parsedUser?.id) return parsedUser.id;

  const token = localStorage.getItem("auth-token");
  const payload = token ? decodeJwtPayload(token) : undefined;
  if (payload?.userId) return payload.userId;
  if (payload?.uid) return payload.uid;
  if (payload?.sub) return payload.sub;
  if (payload?.id) return payload.id;

  return undefined;
}

function previewValue(v, max = 120) {
  const s = typeof v === "string" ? v : JSON.stringify(v);
  if (!s) return "";
  return s.length > max ? s.slice(0, max - 3) + "…" : s;
}

function deltaToDigest(delta, path = []) {
  const lines = [];
  const isLeaf = (d) =>
    Array.isArray(d) && (d.length === 1 || d.length === 2 || d.length === 3);

  function walk(node, p) {
    if (isLeaf(node)) {
      if (node.length === 1) {
        lines.push({ op: "add", path: "/" + p.join("/"), after: previewValue(node[0]) });
      } else if (node.length === 3 && node[1] === 0 && node[2] === 0) {
        lines.push({ op: "remove", path: "/" + p.join("/"), before: previewValue(node[0]) });
      } else if (node.length === 2) {
        lines.push({
          op: "replace",
          path: "/" + p.join("/"),
          before: previewValue(node[0]),
          after: previewValue(node[1]),
        });
      }
      return;
    }
    if (!node || typeof node !== "object") return;
    for (const k of Object.keys(node)) {
      if (k === "_t") continue;
      walk(node[k], p.concat(k));
    }
  }

  walk(delta, path);
  return lines;
}

function normalizePayload(obj) {
  const clone = JSON.parse(JSON.stringify(obj ?? {}));

  const numArrayKeys = new Set(["academyIds", "sportIds", "coachIds", "userIds"]);
  const numKeys = new Set([
    "id",
    "minAmount",
    "maxAmount",
    "percentage",
    "maxUsagePerUser",
    "sportId",
    "coachTypeAcademyId",
  ]);

  function walk(o) {
    if (Array.isArray(o)) {
      o.forEach(walk);
      return;
    }
    if (o && typeof o === "object") {
      Object.keys(o).forEach((k) => {
        const v = o[k];

        if (numKeys.has(k) && v !== null && v !== undefined && v !== "") {
          const n = Number(v);
          if (!Number.isNaN(n)) o[k] = n;
          return;
        }

        if (numArrayKeys.has(k) && Array.isArray(v)) {
          o[k] = v
            .map((x) => Number(x))
            .filter((x) => !Number.isNaN(x))
            .sort((a, b) => a - b);
          return;
        }

        if (typeof v === "object" && v !== null) walk(v);
      });
    }
  }

  walk(clone);
  return clone;
}

export function buildDiffPreview(oldPayload, newPayload, userId) {
  const oldN = normalizePayload(oldPayload || {});
  const newN = normalizePayload(newPayload || {});
  const delta = jdp.diff(oldN, newN);
  const digest = delta ? deltaToDigest(delta) : [];
  const counts = {
    changed: digest.filter((d) => d.op === "replace").length,
    added: digest.filter((d) => d.op === "add").length,
    removed: digest.filter((d) => d.op === "remove").length,
  };

  const items = digest.slice(0, 30).map((l) => {
    if (l.op === "replace")
      return `<li>🟨 <code>${l.path}</code>: <i>${l.before}</i> → <b>${l.after}</b></li>`;
    if (l.op === "add") return `<li>🟩 <code>${l.path}</code>: <b>${l.after}</b></li>`;
    return `<li>🟥 <code>${l.path}</code>: <i>${l.before}</i> (removed)</li>`;
  });

  const html = `
    <div style="text-align:left; font-size:13px; max-height:260px; overflow:auto">
      <div style="margin-bottom:8px">
        <b>${counts.changed}</b> changed · <b>${counts.added}</b> added · <b>${counts.removed}</b> removed
      </div>
      <ul style="padding-left:16px; margin:0">
        ${items.join("")}${digest.length > 30 ? `<li>…and ${digest.length - 30} more</li>` : ""}
      </ul>
    </div>
  `;
  return { delta, digest, html, userId };
}

export async function fireAudit({
  userId: userIdFromArgs, 
  entity,
  affected_id,
  affected_user,
  action,
  section,
  subSection,
  businessApi,
  businessMethod = "POST",
  oldPayload,
  newPayload,
  extra = {},
}) {
  try {
    const token = localStorage.getItem("auth-token");
    const userId = userIdFromArgs ?? getUserId();

    const base = {
      userId,
      entity,
      affected_id,
      affected_user,
      action,
      section,
      subSection,
      businessApi,
      businessMethod,
      ...extra,
    };

    const url = `${process.env.REACT_APP_API_AUDIT_LOG}?loginUserId=${userId ?? ""}&isSuccess=true`;
    const headers = {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    };

    if (String(action ?? "").toLowerCase() === "delete") {
      return await axios.post(url, base, headers);
    }

    const { delta, digest } = buildDiffPreview(oldPayload, newPayload);
    const body = { ...base, delta, digest, old: oldPayload, new: newPayload };
    return await axios.post(url, body, headers);
  } catch (e) {
    console.warn("[AUDIT] send failed", e);
    return null;
  }
}

